import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { EVENT_PAGE_NAMES } from 'state/cdp/constants';
import { useLocationContext } from 'state/location';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import { routes } from 'utils/routing';

import { IBannerReward, LoyaltyRewardsCarousel } from '../loyalty-rewards-carousel';

import {
  HeaderContainer,
  HowItWorksButton,
  StyledLoyaltyTiersLogo,
  StyledPicture,
  UnauthCellContainer,
  UnauthCellDescription,
  UnauthCellDetails,
  UnauthCellTitle,
  UnauthTile,
  UnauthenticatedTierContainer,
} from './styled';

interface ILoyaltyTiersUnauthenticatedBannerProps {
  compact?: boolean;
  hideActionButton?: boolean;
  displaySignupLink?: boolean;
  displayCarousel?: boolean;
}

export const LoyaltyTiersBannerUnauthenticated: FC<ILoyaltyTiersUnauthenticatedBannerProps> = ({
  compact,
  hideActionButton,
  displaySignupLink,
  displayCarousel = true,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();
  const { loading, data, getContentForTier } = useFeaturesLoyaltyTiersContent();
  const location = useLocation();
  const { setEventPageName } = useLocationContext();

  const carouselRewards = getContentForTier(userTier)?.loyaltyTiersCarouselRewards;

  const handleClick = () => {
    setEventPageName(EVENT_PAGE_NAMES.MyBurgerKingLoyaltyTiers);
    navigate(data?.loyaltyTiersHowItWorksButtonLink?.locale!);
  };

  const pathName = location.pathname;

  if (compact) {
    return (
      <UnauthenticatedTierContainer
        compact={true}
        data-testid="unauthenticated-tier-banner-compact"
      >
        <HeaderContainer>
          <StyledLoyaltyTiersLogo center={hideActionButton} compact={compact} inverted />
          {!loading && !hideActionButton && !displaySignupLink && (
            <HowItWorksButton
              size={ActionButtonSizes.SMALL}
              data-testid="unauthenticated-tier-banner-how-it-works-button"
              variant={ActionButtonVariants.OUTLINE}
              onClick={handleClick}
              eventAttributes={{
                Name: 'How it Works',
                Path: pathName,
              }}
            >
              {data?.loyaltyTiersHowItWorksButtonText?.locale}
            </HowItWorksButton>
          )}
          {displaySignupLink && (
            <HowItWorksButton
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              to={routes.signUp}
              data-testid="loyalty-tiers-how-it-works-signup"
              eventAttributes={{
                Name: 'Sign Up',
                Path: pathName,
              }}
            >
              {formatMessage({ id: 'signUp' })}
            </HowItWorksButton>
          )}
        </HeaderContainer>
      </UnauthenticatedTierContainer>
    );
  }

  return (
    <UnauthenticatedTierContainer data-testid="unauthenticated-tier-banner">
      <HeaderContainer>
        <StyledLoyaltyTiersLogo center={hideActionButton} inverted />
        {!loading && !hideActionButton && (
          <HowItWorksButton
            size={ActionButtonSizes.SMALL}
            data-testid="unauthenticated-tier-banner-how-it-works-button"
            variant={ActionButtonVariants.OUTLINE}
            onClick={handleClick}
            eventAttributes={{
              Name: 'How it Works',
              Path: location.pathname,
            }}
          >
            {data?.loyaltyTiersHowItWorksButtonText?.locale}
          </HowItWorksButton>
        )}
      </HeaderContainer>
      {!loading && (
        <>
          <UnauthCellContainer fullRows={hideActionButton}>
            {data?.loyaltyTiersUnauthenticatedCells?.map((tile, id) => (
              <UnauthTile key={id} fullRow={hideActionButton}>
                <StyledPicture
                  image={tile?.loyaltyTiersSignupCellImage?.locale}
                  alt=""
                  objectFitContain
                />
                <UnauthCellDetails>
                  <UnauthCellTitle>{tile?.loyaltyTiersSignupCellTitle?.locale}</UnauthCellTitle>
                  <UnauthCellDescription>
                    {tile?.loyaltyTiersSignupCellDescription?.locale}
                  </UnauthCellDescription>
                </UnauthCellDetails>
              </UnauthTile>
            ))}
          </UnauthCellContainer>
        </>
      )}
      {displayCarousel && carouselRewards?.length ? (
        <LoyaltyRewardsCarousel
          rewards={carouselRewards as unknown as IBannerReward[]}
          currentUserTier={userTier}
        />
      ) : null}
    </UnauthenticatedTierContainer>
  );
};
